// ─────────────────────────────────────────────────────────────────────────────
// exerciseService.js — Phase 2
// CRUD operations for exercises and students.
// All functions are async and throw on error — callers must try/catch.
//
// Session codes (e.g. ROMEO-7): shared with all students, unique across DB.
// Personal codes (e.g. ALPHA-12): unique within one exercise.
// Both use NATO phonetic alphabet words + random number.
// ─────────────────────────────────────────────────────────────────────────────

import { supabase } from './supabaseClient';

// NATO phonetic alphabet — word list for code generation
const NATO = [
  'ALPHA','BRAVO','CHARLIE','DELTA','ECHO','FOXTROT','GOLF','HOTEL',
  'INDIA','JULIET','KILO','LIMA','MIKE','NOVEMBER','OSCAR','PAPA',
  'QUEBEC','ROMEO','SIERRA','TANGO','UNIFORM','VICTOR','WHISKEY',
  'XRAY','YANKEE','ZULU',
];

// Generate a random NATO-style code: WORD-NN (e.g. ROMEO-7)
function makeCode() {
  const word = NATO[Math.floor(Math.random() * NATO.length)];
  const num  = Math.floor(Math.random() * 90) + 10; // 10–99
  return `${word}-${num}`;
}

export const exerciseService = {

  // ── Generate a session code unique across all exercises in the DB ──────────
  async generateSessionCode() {
    let code;
    let attempts = 0;
    do {
      code = makeCode();
      const { count } = await supabase
        .from('exercises')
        .select('id', { count: 'exact', head: true })
        .eq('session_code', code);
      if (count === 0) break;
      attempts++;
    } while (attempts < 20);
    return code;
  },

  // ── Generate a personal code unique within a given exercise ───────────────
  generatePersonalCode(existingCodes = []) {
    let code;
    let attempts = 0;
    do {
      code = makeCode();
      attempts++;
    } while (existingCodes.includes(code) && attempts < 50);
    return code;
  },

  // ── Create a new exercise room ─────────────────────────────────────────────
  // scenarioSnapshot: full Tab 1–6 state object (from scenario prop in App.jsx)
  async createExercise({ name, scenarioSnapshot }) {
    // Explicitly fetch the current user so trainer_id is always set in the
    // INSERT payload. Relying solely on DEFAULT auth.uid() is fragile — the
    // column default is not guaranteed on all Supabase project configurations,
    // and the RLS WITH CHECK (trainer_id = auth.uid()) fails when the value is NULL.
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const sessionCode = await this.generateSessionCode();
    const { data, error } = await supabase
      .from('exercises')
      .insert({
        trainer_id:        user.id,
        session_code:      sessionCode,
        name:              name.trim(),
        scenario_snapshot: scenarioSnapshot ?? null,
        status:            'OPEN',
      })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // ── Get all exercises owned by the current trainer (+ co-organised) ────────
  async getExercises() {
    const { data, error } = await supabase
      .from('exercises')
      .select('id, session_code, name, status, created_at')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  // ── Get one exercise with its full student roster ─────────────────────────
  async getExerciseWithStudents(exerciseId) {
    const { data, error } = await supabase
      .from('exercises')
      .select('*, students(*)')
      .eq('id', exerciseId)
      .single();
    if (error) throw error;
    // Sort students by name for consistent display
    if (data?.students) {
      data.students.sort((a, b) => a.name.localeCompare(b.name));
    }
    return data;
  },

  // ── Add a student to an exercise ─────────────────────────────────────────
  async addStudent(exerciseId, { name, rank, country, personalCode }) {
    const { data, error } = await supabase
      .from('students')
      .insert({
        exercise_id:   exerciseId,
        name:          name.trim(),
        rank:          rank || null,
        country:       country || null,
        personal_code: personalCode,
      })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // ── Remove a student ──────────────────────────────────────────────────────
  async removeStudent(studentId) {
    const { error } = await supabase
      .from('students')
      .delete()
      .eq('id', studentId);
    if (error) throw error;
  },

  // ── Open or close a room ──────────────────────────────────────────────────
  async updateStatus(exerciseId, status) {
    const { data, error } = await supabase
      .from('exercises')
      .update({ status })
      .eq('id', exerciseId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // ── Delete an exercise (CASCADE removes students + sessions) ─────────────
  async deleteExercise(exerciseId) {
    const { error } = await supabase
      .from('exercises')
      .delete()
      .eq('id', exerciseId);
    if (error) throw error;
  },

  // ── P3: Student self-registration — join with session code + name only ────
  // No pre-issued personal code required. The RPC generates one and inserts
  // the student row. Accessible to anon (unauthenticated) students.
  // Returns: { student_id, exercise_id, student_name, rank, country, personal_code, scenario_snapshot }
  async selfRegisterStudent({ sessionCode, name, rank, country }) {
    const { data, error } = await supabase.rpc('self_register_student', {
      p_session_code: sessionCode.trim().toUpperCase(),
      p_name:         name.trim(),
      p_rank:         rank?.trim() || null,
      p_country:      country?.trim() || null,
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data; // { student_id, exercise_id, student_name, personal_code, scenario_snapshot, … }
  },

  // ── P2-S3: Validate student join — calls Supabase RPC (SECURITY DEFINER) ──
  // The RPC runs with elevated DB privileges so anon users can validate codes
  // without direct table access. Returns student data + scenario snapshot.
  async validateStudentJoin(sessionCode, personalCode) {
    const { data, error } = await supabase.rpc('validate_student_join', {
      p_session_code:  sessionCode.trim().toUpperCase(),
      p_personal_code: personalCode.trim().toUpperCase(),
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data; // { student_id, exercise_id, student_name, rank, country, scenario_snapshot }
  },

  // ── P2-S6: Persist student session history to Supabase ───────────────────
  // Uses SECURITY DEFINER RPC so anon/student can write without direct table access.
  // Non-blocking — caller should fire-and-forget with .catch(() => {}).
  async saveStudentSession(studentId, exerciseId, history) {
    const { error } = await supabase.rpc('upsert_student_session', {
      p_student_id:  studentId,
      p_exercise_id: exerciseId,
      p_history:     history,
    });
    if (error) throw error;
  },

  // ── P2-S6: Get all student sessions for an exercise (trainer only) ────────
  async getExerciseSessions(exerciseId) {
    const { data, error } = await supabase
      .from('exercise_sessions')
      .select('*, students(name, rank, country, personal_code)')
      .eq('exercise_id', exerciseId)
      .order('last_active', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  // ── Course Dashboard — aggregate summary across all exercises ────────────
  // Calls get_course_summary() SECURITY DEFINER RPC.
  // Returns array of exercise objects with student_count, session_count,
  // total_decisions, last_active, is_owner fields.
  async getCourseSummary() {
    const { data, error } = await supabase.rpc('get_course_summary');
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data?.exercises || [];
  },

  // ── Co-organiser invite — create ─────────────────────────────────────────
  // Calls SECURITY DEFINER RPC. Returns { token, exercise_name, expires_at }.
  async createCoOrganiserInvite(exerciseId) {
    const { data, error } = await supabase.rpc('create_co_organiser_invite', {
      p_exercise_id: exerciseId,
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data; // { token, exercise_name, expires_at }
  },

  // ── Co-organiser invite — redeem ─────────────────────────────────────────
  // Called by Trainer B after opening the invite link.
  // Returns { exercise_id, exercise_name, success } or throws.
  async redeemCoOrganiserInvite(token) {
    const { data, error } = await supabase.rpc('redeem_co_organiser_invite', {
      p_token: token.trim(),
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data; // { exercise_id, exercise_name, success: true }
  },

  // ── Get co-organisers for an exercise ─────────────────────────────────────
  // Returns array of { trainer_id, added_at, name }.
  async getCoOrganisers(exerciseId) {
    const { data, error } = await supabase.rpc('get_exercise_co_organisers', {
      p_exercise_id: exerciseId,
    });
    if (error) throw error;
    return data || [];
  },

  // ── Cross-student inject decision aggregation (trainer only) ─────────────
  // Calls get_exercise_inject_decisions() SECURITY DEFINER RPC.
  // Returns flat array of { student_id, student_name, rank, inject_id,
  //   inject_label, decision, threat_class, rec_action, confidence }.
  // One row per student per inject — most recent calculation wins.
  async getExerciseInjectDecisions(exerciseId) {
    const { data, error } = await supabase.rpc('get_exercise_inject_decisions', {
      p_exercise_id: exerciseId,
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return Array.isArray(data) ? data : [];
  },

  // ── Parse a CSV roster file ───────────────────────────────────────────────
  // Expected columns (case-insensitive): Name, Rank, Country
  // Returns array of { name, rank, country } objects
  parseRosterCsv(text) {
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) throw new Error('CSV must have a header row and at least one student.');

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const nameIdx    = headers.findIndex(h => h.includes('name'));
    const rankIdx    = headers.findIndex(h => h.includes('rank'));
    const countryIdx = headers.findIndex(h => h.includes('country'));

    if (nameIdx === -1) throw new Error('CSV must have a "Name" column.');

    return lines.slice(1).map(line => {
      const cols = line.split(',').map(c => c.trim().replace(/^"|"$/g, ''));
      return {
        name:    cols[nameIdx]    || '',
        rank:    rankIdx    >= 0 ? (cols[rankIdx]    || '') : '',
        country: countryIdx >= 0 ? (cols[countryIdx] || '') : '',
      };
    }).filter(s => s.name);
  },
};
